import clsx from "clsx";

import { TextField } from "../base/TextField";
import { Select } from "../base/Select";
import { Toggle } from "../base/Toggle";
import {
  ASPECTS_SOURCES,
  ITitleConfigRequest,
  PART_NUMBER_SOURCES,
  PLACEMENT_SOURCES,
  TitleType,
} from "../../models/Category/TitleConfig";

interface BasicSettingsProps {
  config: ITitleConfigRequest;
  onChange: (patch: Partial<ITitleConfigRequest>) => void;
  onTitleTypeChange: (titleType: TitleType) => void;
}

const TITLE_TYPES: TitleType[] = ["Cosmetic", "Mechanical"];

export default function BasicSettings({
  config,
  onChange,
  onTitleTypeChange,
}: BasicSettingsProps) {
  return (
    <section className="bg-white rounded shadow-sm border border-gray-200 p-4">
      <h2 className="text-xs font-semibold tracking-wide text-gray-500 uppercase mb-4">
        Title Type &amp; Basic Settings
      </h2>

      <div className="mb-4">
        <span className="block text-sm font-medium opacity-80 mb-1">
          Title Type
        </span>
        <div className="inline-flex rounded border border-gray-200 overflow-hidden">
          {TITLE_TYPES.map((type) => (
            <button
              key={type}
              type="button"
              onClick={() => onTitleTypeChange(type)}
              className={clsx(
                "px-6 py-2 text-sm",
                config.titleType === type
                  ? "bg-orange-600/10 text-orange-700 font-medium"
                  : "bg-white text-gray-600 hover:bg-gray-50",
              )}
            >
              {type}
            </button>
          ))}
        </div>
        <p className="mt-1 text-xs text-gray-500">
          Switching type offers the matching default priority and removal
          lists. Your other settings are kept.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div>
          <TextField
            fullWidth
            label="Primary Keyword"
            placeholder="Mirror"
            value={config.marketplacePrimaryKeyword ?? ""}
            onChange={(e) =>
              onChange({ marketplacePrimaryKeyword: e.currentTarget.value })
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            The main customer-facing search term for this category.
          </p>
        </div>

        <div>
          <TextField
            fullWidth
            label="Synonyms"
            placeholder="Wing Mirror, Door Mirror, Side Mirror"
            value={config.synonyms ?? ""}
            onChange={(e) => onChange({ synonyms: e.currentTarget.value })}
          />
          <p className="mt-1 text-xs text-gray-500">
            Comma separated, in priority order. Added left to right, removed
            right to left.
          </p>
        </div>

        <div>
          <Select
            fullWidth
            label="Placement Field"
            value={config.placementSource ?? "SideAndPosition"}
            onChange={(e) =>
              onChange({ placementSource: e.currentTarget.value })
            }
          >
            {PLACEMENT_SOURCES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
          <p className="mt-1 text-xs text-gray-500">
            Placement is still derived from Side and Position; a dedicated
            field is not built yet.
          </p>

          <div className="mt-3">
            <Toggle
              labelRight
              label="Prefer Driver/Passenger"
              checked={config.preferDriverPassenger}
              onChange={(value) => onChange({ preferDriverPassenger: value })}
            />
            <p className="mt-1 text-xs text-gray-500">
              Asks the AI for &quot;Driver&quot; and &quot;Passenger&quot;
              wording instead of &quot;Right&quot; and &quot;Left&quot;. Right
              is the driver side, left is the passenger side.
            </p>
          </div>
        </div>

        <div>
          <Select
            fullWidth
            label="Aspects Field"
            value={config.aspectsSource ?? "Aspects"}
            onChange={(e) => onChange({ aspectsSource: e.currentTarget.value })}
          >
            {ASPECTS_SOURCES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <Select
            fullWidth
            label="Part Number Field"
            value={config.partNumberSource ?? "PartNumber"}
            onChange={(e) =>
              onChange({ partNumberSource: e.currentTarget.value })
            }
          >
            {PART_NUMBER_SOURCES.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </Select>
        </div>

        <div>
          <TextField
            fullWidth
            label="eBay Category ID"
            placeholder="33656"
            value={config.ebayCategoryId ?? ""}
            onChange={(e) =>
              onChange({ ebayCategoryId: e.currentTarget.value })
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            The eBay leaf category for this part category. It is what the aspect
            picker asks eBay about; it does not change how anyone lists.
          </p>
        </div>

        <div>
          <TextField
            fullWidth
            type="number"
            label="Title Character Limit"
            min={20}
            max={500}
            value={config.titleCharacterLimit}
            onChange={(e) =>
              onChange({
                titleCharacterLimit: Number(e.currentTarget.value) || 0,
              })
            }
          />
          <p className="mt-1 text-xs text-gray-500">
            eBay UK recommended maximum is 80.
          </p>
        </div>
      </div>
    </section>
  );
}
